import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCharacter } from "./CharacterProvider";
import { encodeCharacterId, decodeCharacterId } from "./characterSlug";
import * as api from "../../utils/api";
import { toast } from 'react-toastify';
import './CharacterSelector.css';

const CREATE_VALUE = '__CREATE_NEW__';

// Funny placeholders
const funnyNames = [
    'Has-No-Name, son of Has-a-Name',
    'HELP I NEED A NAME',
    'P.L. Aceholder',
    'Todo: Name Character',
    'Redacted of Somewhere',
    'Unnamed Squire #1',
    'Generic Adventurer',
    'Lorem Ipsum the Bold',
    'Insert Epic Name',
    'Who-Am-I',
    'Nameless the Untitled'
];

const pickFunny = (prev) => {
    if (!funnyNames.length) return '';
    let choice = funnyNames[Math.floor(Math.random() * funnyNames.length)];
    if (prev && funnyNames.length > 1) {
        while (choice === prev) choice = funnyNames[Math.floor(Math.random() * funnyNames.length)];
    }
    return choice;
};

// Strip diacritics and lowercase, so e.g. "Lómë" matches a search for "lome"
const normalize = (s) =>
    (s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

// Case/diacritic-insensitive, numeric-aware sort
const sortCharacters = (list) =>
    [...(list || [])].sort((a, b) =>
        (a?.name || '').trim().localeCompare((b?.name || '').trim(), undefined, { sensitivity: 'base', numeric: true })
    );

/* =========================
   Combobox (standalone)
   ========================= */
function CharacterComboBox({
                               items,
                               currentName,
                               onChoose,      // async (item) => void
                               onCreateNew,   // () => void
                               disabled = false,
                               showPlayer = false, // staff-only: show the owning player's name
                           }) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState(currentName || '');
    const [highlight, setHighlight] = useState(0);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listId = useId();

    // keep input in sync with external selection
    useEffect(() => {
        setQuery(currentName || '');
    }, [currentName]);

    // Helpers
    const resetToCurrent = () => setQuery(currentName || '');
    const closePanel = ({ reset = false, blur = false } = {}) => {
        setIsOpen(false);
        if (reset) resetToCurrent();
        if (blur) inputRef.current?.blur();
    };

    // click-outside to close + reset text
    // Re-subscribes whenever currentName changes so the stale closure below
    // (captured at effect-creation time) always resets to the latest selection
    // instead of whatever character was selected (or unselected) at mount.
    useEffect(() => {
        const onDocClick = (e) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) {
                closePanel({ reset: true, blur: true });
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentName]);

    const filtered = useMemo(() => {
        const q = normalize(query.trim());
        if (!q) return items;
        return (items || []).filter((c) => {
            const n = normalize(c?.name || '');
            // Only let staff search by the owning player's name.
            const p = showPlayer ? normalize(c?.playerName || '') : '';
            return n.includes(q) || (p && p.includes(q));
        });
    }, [items, query, showPlayer]);

    useEffect(() => {
        if (!isOpen) return;
        setHighlight((h) => Math.max(0, Math.min(h, filtered.length)));
    }, [isOpen, filtered.length]);

    // open, but don't refocus if already focused
    const open = () => {
        setIsOpen(true);
        if (document.activeElement !== inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    // Opening the picker clears the current text and shows the full list, so you
    // can jump to a different character without first deleting the selected name.
    const openFresh = () => {
        open();
        setQuery('');
        setHighlight(0);
    };

    const chooseIndex = async (idx) => {
        if (idx === 0) {
            // Create new
            setQuery('');                    // clear input while modal opens
            closePanel({ blur: true });      // blur so cursor stops blinking
            onCreateNew?.();
            return;
        }
        const item = filtered[idx - 1];
        if (!item) return;
        setQuery(item.name);               // reflect the choice immediately
        await onChoose?.(item);            // will also update currentName upstream
        closePanel({ blur: true });        // blur after a selection
    };

    const onKeyDown = async (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIsOpen(true);
            setHighlight((h) => Math.min(h + 1, filtered.length));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIsOpen(true);
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            await chooseIndex(highlight);
        } else if (e.key === 'Escape') {
            // cancel -> close and reset to last chosen name, keep focus
            e.preventDefault();
            closePanel({ reset: true, blur: false });
        }
    };

    return (
        <div
            className={`combo ${showPlayer ? 'combo--two-col' : ''}`}
            ref={containerRef}
            onBlur={(e) => {
                // if focus leaves the combobox entirely, close & reset
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    closePanel({ reset: true, blur: false });
                }
            }}
        >
            <div className="combo-input-wrap">
                <input
                    ref={inputRef}
                    type="text"
                    className="combo-input"
                    role="combobox"
                    aria-label="Character"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    placeholder="Select a Character…"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                        setHighlight(0);
                    }}
                    onFocus={openFresh}
                    onClick={openFresh}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                />
                <button
                    type="button"
                    className={`combo-caret ${isOpen ? 'is-open' : ''}`}
                    aria-label={isOpen ? 'Close character list' : 'Open character list'}
                    onClick={() =>
                        isOpen ? closePanel({ reset: true, blur: false }) : open()
                    }
                    disabled={disabled}
                />
            </div>

            {isOpen && (
                <div className="combo-panel" role="listbox" id={listId} tabIndex={-1}>
                    <div
                        className={'combo-option create ' + (highlight === 0 ? 'is-highlighted' : '')}
                        role="option"
                        aria-selected={highlight === 0}
                        onMouseEnter={() => setHighlight(0)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => chooseIndex(0)}
                    >
                        <span className="name">+ Create New Character…</span>
                    </div>

                    {filtered.map((c, i) => {
                        const idx = i + 1; // offset for create row
                        return (
                            <div
                                key={c.characterId}
                                className={'combo-option ' + (highlight === idx ? 'is-highlighted' : '')}
                                role="option"
                                aria-selected={highlight === idx}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => chooseIndex(idx)}
                                title={showPlayer && c.playerName ? `${c.name} — ${c.playerName}` : c.name}
                            >
                                <span className="name">{c.name}</span>
                                {showPlayer && <span className="player">{c.playerName || ''}</span>}
                            </div>
                        );
                    })}

                    {!filtered.length && (
                        <div className="combo-empty">No matches. Press Enter to create.</div>
                    )}
                </div>
            )}
        </div>
    );
}


/* =========================
   Main component
   ========================= */
const CharacterSelector = ({ token, playerId, staffPermissions }) => {
    const [characters, setCharacters] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newCharName, setNewCharName] = useState('');
    const [placeholderName, setPlaceholderName] = useState(pickFunny(''));
    const inputRef = useRef(null);

    const { currentCharacter, setCurrentCharacter } = useCharacter();
    const navigate = useNavigate();
    const { slug } = useParams();

    // The name shown in the combobox. Kept separate from currentCharacter so it
    // doesn't blank out while switching characters — currentCharacter is cleared
    // immediately (so stale data can't flash), but the box should keep showing
    // whichever name the user just picked until the new character is ready.
    const [selectedName, setSelectedName] = useState('');
    useEffect(() => {
        if (currentCharacter?.name) setSelectedName(currentCharacter.name);
    }, [currentCharacter?.name]);

    useEffect(() => {
        (async function () {
            try {
                const list = await api.fetchCharacters();
                setCharacters(sortCharacters(list));
            } catch {
                toast.error('Failed to load characters.');
            }
        })();
    }, [token]);

    // Load the character referenced by the URL slug (deep link / page refresh).
    // The URL is the source of truth: when the slug points at a character other
    // than the one already loaded, fetch it. Choosing/creating updates the URL,
    // which in turn flows back through here — the id guard prevents a reload loop.
    useEffect(() => {
        const idFromUrl = decodeCharacterId(slug);
        if (idFromUrl == null) return;
        if (currentCharacter?.characterId === idFromUrl) return;
        const known = characters.find((c) => c.characterId === idFromUrl);
        if (known?.name) setSelectedName(known.name);
        (async () => {
            try {
                await loadAndSetCharacter(idFromUrl);
                setSelectedId(String(idFromUrl));
            } catch {
                toast.error('Character not found.');
                navigate('/character', { replace: true });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, currentCharacter?.characterId]);

    // Landing on /character with no slug: default to this player's character —
    // the same one the sidebar shows (their Primary character). We redirect to
    // its slug so the URL reflects the active character like any other
    // selection; the slug effect above then loads it.
    useEffect(() => {
        if (slug || currentCharacter || playerId == null) return;
        let cancelled = false;
        (async () => {
            try {
                const list = await api.fetchCharactersByPlayer(playerId);
                const primary = Array.isArray(list)
                    ? (list.find((c) => c.characterStatus === 'Primary') || list[0])
                    : null;
                if (cancelled || !primary?.characterId) return;
                navigate(`/character/${encodeCharacterId(primary.characterId)}`, { replace: true });
            } catch {
                // No default available — leave the selector empty.
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, playerId, currentCharacter]);

    useEffect(() => {
        setSelectedId(currentCharacter?.characterId ? String(currentCharacter.characterId) : '');
    }, [currentCharacter?.characterId]);

    useEffect(() => {
        if (showCreateModal && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [showCreateModal]);

    const loadAndSetCharacter = async (characterId) => {
        const characterData = await api.fetchCharacter(characterId);

        if (characterData.transformId) {
            try {
                const t = await api.fetchTransform(characterData.transformId);
                characterData.transformName = t?.name;
            } catch {}
        }

        if (characterData.playerId) {
            try {
                const p = await api.fetchPlayer(characterData.playerId);
                characterData.playerName = p?.name || 'Guest';
            } catch {}
        }

        setCurrentCharacter(characterData);
    };

    const openCreateModal = () => {
        setNewCharName('');
        setPlaceholderName((prev) => pickFunny(prev));
        setShowCreateModal(true);
    };
    const closeCreateModal = () => {
        setShowCreateModal(false);
        setIsCreating(false);
        setNewCharName('');
    };

    const confirmCreate = async () => {
        const name = newCharName.trim();
        if (!name) {
            toast.warn('Please enter a name.');
            return;
        }
        try {
            setIsCreating(true);
            const created = await api.createCharacter({ name });
            const list = await api.fetchCharacters();
            const sorted = sortCharacters(list);
            setCharacters(sorted);
            const newId = created?.characterId ?? sorted.find((c) => c.name === name)?.characterId;
            if (!newId) return toast.error('Character created, but could not load it.');
            setSelectedName(name);
            await loadAndSetCharacter(newId);
            setSelectedId(String(newId));
            navigate(`/character/${encodeCharacterId(newId)}`);
            toast.success(`Created "${name}"`);
            closeCreateModal();
        } catch {
            toast.error('Failed to create character.');
        } finally {
            setIsCreating(false);
        }
    };

    const handleOverlayMouseDown = (e) => {
        if (e.target === e.currentTarget) closeCreateModal();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); closeCreateModal(); }
        else if (e.key === 'Enter') { e.preventDefault(); confirmCreate(); }
    };

    return (
        <div className="character-select-container">
            <div className="selector-row">
                <CharacterComboBox
                    items={characters}
                    currentName={selectedName}
                    onChoose={async (item) => {
                        setSelectedName(item.name);
                        await loadAndSetCharacter(item.characterId);
                        setSelectedId(String(item.characterId));
                        navigate(`/character/${encodeCharacterId(item.characterId)}`);
                    }}
                    onCreateNew={() => {
                        setSelectedId('');
                        openCreateModal();
                    }}
                    disabled={isCreating}
                    showPlayer={!!staffPermissions}
                />
            </div>

            {showCreateModal && (
                <div
                    className="create-modal-overlay"
                    onMouseDown={handleOverlayMouseDown}
                    onKeyDown={handleKeyDown}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="createCharacterTitle"
                >
                    <div className="create-modal" onMouseDown={(e) => e.stopPropagation()}>
                        <h3 id="createCharacterTitle" className="modal-title">Create New Character</h3>
                        <label className="modal-label" htmlFor="newCharacterName">Name</label>
                        <input
                            id="newCharacterName"
                            ref={inputRef}
                            type="text"
                            className="modal-input"
                            value={newCharName}
                            onChange={(e) => setNewCharName(e.target.value)}
                            placeholder={placeholderName}
                        />

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-btn modal-primary"
                                onClick={confirmCreate}
                                disabled={isCreating || !newCharName.trim()}
                            >
                                {isCreating ? 'Creating…' : 'Create'}
                            </button>
                            <button
                                type="button"
                                className="modal-btn modal-secondary"
                                onClick={closeCreateModal}
                                disabled={isCreating}
                            >
                                Cancel
                            </button>
                        </div>

                        <p className="modal-hint">Press Enter to create, Esc to cancel.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CharacterSelector;
